<template>
  <div class="p-page-wrapper">
    <template v-if="!currentProjectId">
      <v-row>
        <v-col>
          <p-no-data
            :text="$t('noProjects')"
            :hide-btn="true"
            img="event.png"
            class="p-no-data-mt"
          ></p-no-data>
        </v-col>
      </v-row>
    </template>
    <template v-else>
      <div class="p-toolbar">
        <div class="p-toolbar-item p-search">
          <p-search-input
            v-model="search"
            :items="searchSuggestions"
            @submit="viewMode='grid'"
          ></p-search-input>
        </div>
        <div class="p-toolbar-item">
          <v-btn-toggle
            v-model="viewMode"
            color="primary"
            group
            mandatory
            dense
          >
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  value="grid"
                >
                  <v-icon>mdi-grid</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('event.viewMode.grid') }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  value="calendar"
                >
                  <v-icon>mdi-calendar-blank-outline</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('event.viewMode.calendar') }}</span>
            </v-tooltip>
          </v-btn-toggle>
        </div>
        <div v-if="hasAccess()" class="p-toolbar-item p-right">
          <v-btn
            class="p-btn-rounded mr-4"
            large
            rounded
            @click.stop="openEditDialog(null, false)"
          >
            {{ $t('event.createEvent') }}
          </v-btn>
          <v-btn
            color="primary"
            class="p-btn-text"
            text
            large
            @click="labelsPanel = true"
          >
            {{ $t('label.manageLabels') }}
            <v-icon class="ml-2">mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </div>

      <v-row v-if="viewMode === 'grid'">
        <v-col>
          <events-grid-view
            :search="search"
            @create-event="openEditDialog(null, false)"
            @edit-event="editEvent"
            @copy-event="copyEvent"
            @delete-event="openDeleteDialog"
          ></events-grid-view>
        </v-col>
      </v-row>

      <v-row v-else>
        <v-col>
          <events-calendar-view></events-calendar-view>
        </v-col>
      </v-row>
    </template>

    <event-edit-dialog
      :dialog.sync="editDialog"
      :event="editedEvent"
      @more-options="(event) => openEditDialog(event, true)"
    ></event-edit-dialog>

    <event-edit-dialog-advanced
      :dialog.sync="editDialogAdvanced"
      :event="editedEvent"
      :copy="editDialogCopy"
    ></event-edit-dialog-advanced>

    <event-labels-panel
      :panel.sync="labelsPanel"
    ></event-labels-panel>

    <p-delete-dialog
      :dialog.sync="deleteDialog"
      :item="deletedEvent"
      :item-data="deletedEventData"
      :load-relations="loadRelations"
      :delete-item="deleteEvent"
      @itemDeleted="onItemDeleted"
    ></p-delete-dialog>
  </div>
</template>


<script>
import { mapGetters, mapActions } from 'vuex'

import PSearchInput from '@/components/PSearchInput'
import PNoData from '@/components/PNoData'
import PDeleteDialog from '@/components/PDeleteDialog'

import EventsCalendarView from './EventsCalendarView'
import EventsGridView from './EventsGridView'
import EventEditDialog from './EventEditDialog'
import EventEditDialogAdvanced from './EventEditDialogAdvanced'
import EventLabelsPanel from './EventLabelsPanel'

import snackbarMixin from '@/mixins/snackbar.mixin'
import pageMixin from '@/mixins/page.mixin'
import accessMixin from '@/mixins/access.mixin'

import eventBus from '@/eventBus'


export default {
  components: {
    PSearchInput,
    PNoData,
    PDeleteDialog,
    EventsCalendarView,
    EventsGridView,
    EventEditDialog,
    EventEditDialogAdvanced,
    EventLabelsPanel
  },


  mixins: [
    snackbarMixin,
    pageMixin,
    accessMixin
  ],


  data () {
    return {
      viewMode: 'grid',
      search: '',
      editDialog: false,
      editDialogAdvanced: false,
      editDialogCopy: false,
      editedEvent: null,
      deleteDialog: false,
      deletedEvent: null,
      deletedEventData: null,
      labelsPanel: false,
      eventsLoaded: false
    }
  },


  computed: {
    ...mapGetters('event', ['eventsByProject', 'eventDetailsById']),
    ...mapGetters('eventLabel', ['eventLabels']),
    ...mapGetters('eventCategory', ['eventCategories']),
    ...mapGetters('project', ['currentProjectId']),

    searchSuggestions () {
      const items = this.eventsByProject(this.currentProjectId).map(event => { return event.title })

      for (const label of this.eventLabels) {
        items.push(label.name)
      }

      for (const category of this.eventCategories) {
        items.push(category.name)
      }

      return items
    }
  },


  watch: {
    async currentProjectId () {
      if (!this.currentProjectId) return false
      this.startPageLoading()
      await this.loadEventsByProject(this.currentProjectId)
      this.stopPageLoading()
    },

    editDialogAdvanced (newVal) {
      if (!newVal) {
        this.editDialogCopy = false
      }
    }
  },


  methods: {
    ...mapActions('event', ['loadEventsByProject', 'loadEventDetailsById', 'loadRelations', 'deleteEvent']),
    ...mapActions('member', ['loadMembersByProject']),
    ...mapActions('eventLabel', ['loadEventLabels']),
    ...mapActions('eventCategory', ['loadEventCategories']),
    ...mapActions('contact', ['loadShortContacts']),

    initEventBus () {
      eventBus.$on('goToEntity', (entityTitle) => {
        this.search = entityTitle
      })

      eventBus.$on('loadEntities', async () => {
        await this.loadEventsByProject(this.currentProjectId)
      })
    },

    async editEvent (event) {
      try {
        let eventDetails = this.eventDetailsById(event.id)
        if (!eventDetails) {
          this.startPageLoading()
          eventDetails = await this.loadEventDetailsById(event)
        }
        this.openEditDialog(eventDetails, true)
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      } finally {
        this.stopPageLoading()
      }
    },

    async copyEvent (event) {
      try {
        let eventDetails = this.eventDetailsById(event.id)
        if (!eventDetails) {
          this.startPageLoading()
          eventDetails = await this.loadEventDetailsById(event)
        }
        this.editDialogCopy = true
        this.openEditDialog(eventDetails, true)
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      } finally {
        this.stopPageLoading()
      }
    },

    openEditDialog (eventDetails, advanced) {
      this.editedEvent = eventDetails
      if (advanced) {
        this.editDialogAdvanced = true
      } else {
        this.editDialog = true
      }
    },

    openDeleteDialog (event) {
      try {
        if (event) {
          this.deletedEvent = event
          this.deletedEventData = {
            type: this.$t('entity.event'),
            title: this.deletedEvent.title
          }
        }
        this.deleteDialog = true
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    },

    onItemDeleted () {
      this.showSnackbarSuccess(this.$t('event.eventDeleted'))
    }
  },


  async created () {
    this.setPageTitle(this.$t('nav.events'))
    this.initEventBus()
    if (!this.currentProjectId) return false
    try {
      this.startPageLoading()
      await Promise.all([
        this.loadShortContacts(),
        this.loadEventLabels(),
        this.loadEventCategories()
      ])
      await this.loadEventsByProject(this.currentProjectId)
      await this.loadMembersByProject(this.currentProjectId)
      if (this.$router.currentRoute.params?.goto) {
        this.search = this.$router.currentRoute.params?.goto
      }
    } catch (error) {
      this.showSnackbarError()
      this.$log.error(error)
    } finally {
      this.stopPageLoading()
    }
  }
}
</script>
